import type {
  CheckoutSessionPayload,
  CheckoutSessionResponse,
  Coupon,
  CreateCouponPayload,
  Entitlement,
  PlansResponse,
  PortalSessionResponse,
  UsageDayDetailResponse,
  UsageHistoryResponse,
  UsageSnapshot,
} from "./billing.types";

import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";

/** Public — returns `{ stripeConfigured, plans: Plan[] }`. */
export const fetchPlans = () => {
  return httpClient<PlansResponse>(
    httpMethods.GET,
    API_ENDPOINTS.BILLING.plans,
  );
};

/** Authed — returns the current org's flat entitlement payload. */
export const fetchSubscription = () => {
  return httpClient<Entitlement>(
    httpMethods.GET,
    API_ENDPOINTS.BILLING.subscription,
  );
};

/** Authed — usage snapshot (limits + today's counts + capacity). */
export const fetchUsage = () => {
  return httpClient<UsageSnapshot>(
    httpMethods.GET,
    API_ENDPOINTS.BILLING.usage,
  );
};

/** Authed + org owner/admin. Returns the Stripe Checkout URL to redirect to. */
export const createCheckoutSession = (payload: CheckoutSessionPayload) => {
  return httpClient<CheckoutSessionResponse>(
    httpMethods.POST,
    API_ENDPOINTS.BILLING.checkoutSession,
    payload,
  );
};

/** Authed + org owner. Returns the Stripe Customer Portal URL. */
export const createPortalSession = () => {
  return httpClient<PortalSessionResponse>(
    httpMethods.POST,
    API_ENDPOINTS.BILLING.portalSession,
  );
};

/** Super-admin only. List all coupons. */
export const fetchCoupons = () => {
  return httpClient<Coupon[]>(httpMethods.GET, API_ENDPOINTS.BILLING.coupons);
};

/** Super-admin only. Create a new coupon. Backend echoes the created row. */
export const createCoupon = (payload: CreateCouponPayload) => {
  return httpClient<Coupon>(
    httpMethods.POST,
    API_ENDPOINTS.BILLING.coupons,
    payload,
  );
};

/** Authed — last N days of usage per metric per day (default 30). */
export const fetchUsageHistory = (days = 30) => {
  return httpClient<UsageHistoryResponse>(
    httpMethods.GET,
    `${API_ENDPOINTS.BILLING.usageHistory}?days=${days}`,
  );
};

/** Authed — per-action AI breakdown for a specific date (YYYY-MM-DD). */
export const fetchUsageDayDetail = (date: string) => {
  return httpClient<UsageDayDetailResponse>(
    httpMethods.GET,
    API_ENDPOINTS.BILLING.usageDayDetail(date),
  );
};
